import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { AppUpdateModalComponent } from '../components/app-update-modal/app-update-modal.component';

/**
 * Service that handles forced app updates.
 * When the backend responds with requiresAppUpdate=true, this service shows
 * a blocking modal that requires the user to refresh (web) or update via app store (native).
 */
@Injectable({
  providedIn: 'root'
})
export class ForcedUpdateService {
  /** Guard to prevent multiple modals from being shown simultaneously */
  private isForceUpdateActive = false;

  constructor(private modalController: ModalController) {}

  /**
   * Check if a force update is currently active (modal is showing)
   */
  get isActive(): boolean {
    return this.isForceUpdateActive;
  }

  /**
   * Check response for requiresAppUpdate and show modal if needed.
   * @param response Any API response that may contain requiresAppUpdate
   * @returns true if update is required (caller should stop processing), false otherwise
   */
  checkAndShow(response: any): boolean {
    if (response?.requiresAppUpdate && !this.isForceUpdateActive) {
      this.showUpdateModal(
        response.minimumAppVersion || '',
        response.updateMessage || 'A new version is required. Please update to continue.'
      );
      return true;
    }
    return false;
  }

  /**
   * Shows the blocking update modal. Only one modal can be shown at a time.
   * @param minimumAppVersion The minimum version required
   * @param updateMessage The message from the backend
   */
  async showUpdateModal(minimumAppVersion: string, updateMessage: string): Promise<void> {
    // Prevent duplicate modals
    if (this.isForceUpdateActive) {
      return;
    }

    this.isForceUpdateActive = true;

    const modal = await this.modalController.create({
      component: AppUpdateModalComponent,
      componentProps: {
        minimumAppVersion,
        updateMessage
      },
      backdropDismiss: false, // Cannot dismiss by clicking backdrop
      canDismiss: false, // Cannot dismiss at all (no swipe, no back button)
      cssClass: 'force-update-modal'
    });

    await modal.present();

    // The modal never dismisses normally - user must refresh or go to app store
    // But we handle onDidDismiss in case it somehow gets dismissed
    await modal.onDidDismiss();
    this.isForceUpdateActive = false;
  }
}
